import { isAxiosError } from 'axios';
import { AlertCircle, CheckCircle2, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import {
  useConditionTemplates,
  useConditions,
  useCreateCondition,
  useSatisfyCondition,
  useWaiveCondition,
} from '@/hooks/use-conditions';
import { toast } from '@/stores/toast-store';

interface Props {
  loanId: string;
}

const CONDITION_STATUS_COLOR: Record<string, string> = {
  OUTSTANDING: 'bg-amber-100 text-amber-800',
  SATISFIED: 'bg-green-100 text-green-800',
  WAIVED: 'bg-gray-100 text-gray-800',
  OVERDUE: 'bg-red-100 text-red-800',
  NOT_APPLICABLE: 'bg-gray-100 text-gray-800',
};

function toIsoDateOnly(dateValue: string): string {
  return new Date(`${dateValue}T00:00:00.000Z`).toISOString();
}

export function ConditionsTab({ loanId }: Props) {
  const { data: allConditionsRaw, isLoading } = useConditions(loanId);
  const { data: templatesRaw } = useConditionTemplates();
  const createCondition = useCreateCondition();
  const satisfyCondition = useSatisfyCondition();
  const waiveCondition = useWaiveCondition();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [satisfyingId, setSatisfyingId] = useState<string | null>(null);
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverConditionId, setWaiverConditionId] = useState<string | null>(null);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [dueDateError, setDueDateError] = useState('');

  const [formData, setFormData] = useState({
    description: '',
    category: 'DOCUMENTATION' as const,
    type: 'PRECEDENT' as const,
    dueDate: '',
    notes: '',
  });

  const allConditions = Array.isArray(allConditionsRaw) ? allConditionsRaw : [];
  const templates = Array.isArray(templatesRaw) ? templatesRaw : [];

  const precedentConditions = useMemo(
    () => allConditions.filter(c => c.type === 'PRECEDENT'),
    [allConditions],
  );

  const subsequentConditions = useMemo(
    () => allConditions.filter(c => c.type === 'SUBSEQUENT'),
    [allConditions],
  );

  const satisfiedCount = useMemo(
    () => allConditions.filter(c => c.status === 'SATISFIED').length,
    [allConditions],
  );

  const totalPrecedents = precedentConditions.length;
  const progressPercent = totalPrecedents > 0 ? (satisfiedCount / totalPrecedents) * 100 : 0;

  function handleAddCondition() {
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    if (!formData.dueDate) {
      setDueDateError('Due date is required');
      toast.error('Due date is required');
      return;
    }

    const dueDateIso = toIsoDateOnly(formData.dueDate);

    createCondition.mutate(
      {
        loanId,
        ...formData,
        dueDate: dueDateIso,
        status: 'OUTSTANDING' as const,
      },
      {
        onSuccess: () => {
          toast.success('Condition added');
          setDueDateError('');
          setFormData({
            description: '',
            category: 'DOCUMENTATION',
            type: 'PRECEDENT',
            dueDate: '',
            notes: '',
          });
          setShowAddModal(false);
        },
        onError: error => {
          if (isAxiosError(error)) {
            const validationErrors =
              (
                error.response?.data as
                  { error?: { details?: { validationErrors?: string[] } } } | undefined
              )?.error?.details?.validationErrors ?? [];
            const dueDateValidationError = validationErrors.find(message =>
              message.toLowerCase().includes('duedate'),
            );
            if (dueDateValidationError) {
              setDueDateError('Due date must be a valid ISO 8601 date string');
              return;
            }
          }
          toast.error('Failed to add condition');
        },
      },
    );
  }

  function handleAddFromTemplates() {
    if (selectedTemplates.length === 0) {
      toast.error('Select at least one template');
      return;
    }

    const toAdd = templates.filter(t => selectedTemplates.includes(t.id));
    let count = 0;

    toAdd.forEach(template => {
      createCondition.mutate(
        {
          loanId,
          description: template.description,
          category: template.category,
          type: template.type,
          status: 'OUTSTANDING' as const,
          dueDate: template.commonDueDate
            ? new Date(Date.now() + template.commonDueDate * 86400000).toISOString()
            : undefined,
        },
        {
          onSuccess: () => {
            count++;
            if (count === toAdd.length) {
              toast.success(`Added ${count} condition(s)`);
              setSelectedTemplates([]);
              setShowTemplateModal(false);
            }
          },
        },
      );
    });
  }

  function handleSatisfy(id: string) {
    satisfyCondition.mutate(
      { loanId, conditionId: id },
      {
        onSuccess: () => {
          toast.success('Condition satisfied');
          setSatisfyingId(null);
        },
      },
    );
  }

  function handleWaive(id: string) {
    if (!waiverReason.trim()) {
      toast.error('Waiver reason is required');
      return;
    }

    waiveCondition.mutate(
      { loanId, conditionId: id, reason: waiverReason },
      {
        onSuccess: () => {
          toast.success('Condition waived');
          setWaiverConditionId(null);
          setWaiverReason('');
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2 text-gray-400" />
        <span className="text-gray-400">Loading conditions…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Bar for Precedent Conditions */}
      {totalPrecedents > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-900">Conditions Precedent Progress</p>
            <p className="text-sm font-semibold text-gray-600">
              {satisfiedCount} / {totalPrecedents}
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Conditions</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(true)}
            className="flex items-center gap-1.5 w-fit"
          >
            <Plus className="w-3.5 h-3.5" />
            From Template
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 w-fit"
          >
            <Plus className="w-3.5 h-3.5" />
            Custom
          </Button>
        </div>
      </div>

      {/* Conditions Sections */}
      {precedentConditions.length > 0 || subsequentConditions.length > 0 ? (
        <div className="space-y-6">
          {/* Conditions Precedent */}
          {precedentConditions.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Conditions Precedent</h4>
              {precedentConditions.map(condition => (
                <div
                  key={condition.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{condition.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                          {condition.category}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${CONDITION_STATUS_COLOR[condition.status]}`}
                        >
                          {condition.status}
                        </span>
                      </div>
                      {condition.dueDate && (
                        <p className="text-xs text-gray-500 mt-2">
                          Due: {new Date(condition.dueDate).toLocaleDateString('en-AU')}
                        </p>
                      )}
                      {condition.notes && (
                        <p className="text-xs text-gray-600 mt-2 italic">{condition.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {condition.status === 'OUTSTANDING' && (
                        <>
                          <Button
                            variant="plain"
                            onClick={() => setSatisfyingId(condition.id)}
                            className="inline-flex items-center gap-1 w-fit text-xs font-medium
                                       text-green-600 hover:bg-green-50 rounded transition-colors"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Satisfy
                          </Button>
                          <Button
                            variant="plain"
                            onClick={() => setWaiverConditionId(condition.id)}
                            className="inline-flex items-center gap-1 w-fit text-xs font-medium
                                       text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          >
                            <AlertCircle className="w-3.5 h-3.5" />
                            Waive
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Conditions Subsequent */}
          {subsequentConditions.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Conditions Subsequent</h4>
              {subsequentConditions.map(condition => (
                <div
                  key={condition.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{condition.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                          {condition.category}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${CONDITION_STATUS_COLOR[condition.status]}`}
                        >
                          {condition.status}
                        </span>
                      </div>
                      {condition.dueDate && (
                        <p className="text-xs text-gray-500 mt-2">
                          Due: {new Date(condition.dueDate).toLocaleDateString('en-AU')}
                        </p>
                      )}
                      {condition.notes && (
                        <p className="text-xs text-gray-600 mt-2 italic">{condition.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {condition.status === 'OUTSTANDING' && (
                        <>
                          <Button
                            variant="plain"
                            onClick={() => setSatisfyingId(condition.id)}
                            className="inline-flex items-center gap-1 w-fit text-xs font-medium
                                       text-green-600 hover:bg-green-50 rounded transition-colors"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Satisfy
                          </Button>
                          <Button
                            variant="plain"
                            onClick={() => setWaiverConditionId(condition.id)}
                            className="inline-flex items-center gap-1 w-fit text-xs font-medium
                                       text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          >
                            <AlertCircle className="w-3.5 h-3.5" />
                            Waive
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No conditions added yet</p>
        </div>
      )}

      {/* Add Custom Condition Modal */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Custom Condition"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Condition description…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              >
                <option>DOCUMENTATION</option>
                <option>COMPLIANCE</option>
                <option>SECURITY</option>
                <option>INSURANCE</option>
                <option>VALUATION</option>
                <option>LEGAL</option>
                <option>FINANCIAL</option>
                <option>OTHER</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              >
                <option>PRECEDENT</option>
                <option>SUBSEQUENT</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Due Date *</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={e => {
                setFormData({ ...formData, dueDate: e.target.value });
                if (dueDateError) setDueDateError('');
              }}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            {dueDateError && <p className="mt-1 text-xs text-red-600">{dueDateError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes…"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowAddModal(false)} className="w-fit">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddCondition}
              disabled={createCondition.isPending}
              className="flex items-center gap-2 w-fit"
            >
              {createCondition.isPending && (
                <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
              )}
              Add Condition
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add From Template Modal */}
      <Modal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Add from Template"
      >
        <div className="space-y-4">
          {templates.length === 0 ? (
            <p className="text-sm text-gray-500">No templates available</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {templates.map(template => (
                <label
                  key={template.id}
                  className="flex items-start gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTemplates.includes(template.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedTemplates([...selectedTemplates, template.id]);
                      } else {
                        setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                      }
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{template.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                        {template.category}
                      </span>
                      <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                        {template.type}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setShowTemplateModal(false)}
              className="w-fit"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddFromTemplates}
              disabled={createCondition.isPending || selectedTemplates.length === 0}
              className="flex items-center gap-2 w-fit"
            >
              {createCondition.isPending && (
                <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
              )}
              Add {selectedTemplates.length} Selected
            </Button>
          </div>
        </div>
      </Modal>

      {/* Satisfy Modal */}
      {satisfyingId && (
        <Modal
          open={!!satisfyingId}
          onClose={() => setSatisfyingId(null)}
          title="Satisfy Condition"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Mark this condition as satisfied?</p>
            <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
              <Button variant="secondary" onClick={() => setSatisfyingId(null)} className="w-fit">
                Cancel
              </Button>
              <Button
                variant="plain"
                onClick={() => satisfyingId && handleSatisfy(satisfyingId)}
                disabled={satisfyCondition.isPending}
                className="flex items-center gap-2 w-fit bg-green-600 text-white rounded-md text-sm font-medium
                           hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {satisfyCondition.isPending && (
                  <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
                )}
                Satisfy
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Waive Modal */}
      {waiverConditionId && (
        <Modal
          open={!!waiverConditionId}
          onClose={() => setWaiverConditionId(null)}
          title="Waive Condition"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Waiver Reason *
              </label>
              <textarea
                value={waiverReason}
                onChange={e => setWaiverReason(e.target.value)}
                placeholder="Reason for waiving this condition…"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
              />
            </div>

            <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setWaiverConditionId(null)}
                className="w-fit"
              >
                Cancel
              </Button>
              <Button
                variant="plain"
                onClick={() => waiverConditionId && handleWaive(waiverConditionId)}
                disabled={waiveCondition.isPending}
                className="flex items-center gap-2 w-fit bg-amber-600 text-white rounded-md text-sm font-medium
                           hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {waiveCondition.isPending && (
                  <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
                )}
                Waive Condition
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
